import API from './api';
import { getMyEnrolments } from './testSystem';
import { SIGNED_OUT_LINE, isAuthenticated, isSignedOutError } from '../utils/auth';

/**
 * The workspace's reads, in one place.
 *
 * The workspace opens on a single round trip. `GET work-trials/{id}/workspace-bootstrap`
 * (owned by the candidate backend) answers with everything the shell is keyed on
 * — the candidate-safe trial, its frozen brand kit, the cycle's version label and
 * display timezone, the candidate's profile, the server-computed `available_pages`,
 * the onboarding state, the PII-nudge config and the channel/roster metadata — where
 * the legacy page issues five serial fetches and then re-reads `/me/enrolments` for
 * the two cycle facts it could not find on the trial (`WorkTrial.js:1293-1301`).
 *
 * That enrolment read survives here as a fallback and only as a fallback: it is the
 * source that silently loses the version label and the display timezone once an
 * enrolment ages out of the response, which is the defect the bootstrap exists to
 * close. Nothing else in the workspace polls; the refresh contract owns cadence.
 */

/**
 * For requests that must never bounce the reader to /login.
 *
 * The 401 interceptor (`api.js:35-51`) redirects by default, which is right for a
 * page load and wrong for anything typed: a transient 401 on a composer send would
 * throw away the draft that caused it. Requests carrying this get the original
 * error back and decide for themselves.
 */
export const KEEP_DRAFT = { skipAuthRedirect: true };

/** The bootstrap route, written once so nothing else spells it. */
export const bootstrapPath = (workTrialId) => `work-trials/${workTrialId}/workspace-bootstrap`;

/**
 * The two cycle facts a cycle trial is read with, from either source that can
 * carry them — the bootstrap's cycle block or an enrolment row. A job trial has
 * neither, and says so with empty strings rather than with a missing object.
 * @param {Object} cycle - A cycle as the bootstrap or /me/enrolments projects it
 * @returns {{displayTimezone: string}}
 */
/**
 * The server's verdict on whether this workspace still takes writes.
 *
 * It is read rather than re-derived. The API refuses a write on an ended cycle trial
 * with `409 {code: "trial_ended"}`, and a banner drawn from a *different* reading of
 * the same question — this client's clock against `ends_at` — would disagree with
 * those refusals every time the two clocks did. So the server sends its answer and
 * this normalises it; the clock's own reading survives beside it only to catch a
 * window that closes mid-session, which no bootstrap field can.
 *
 * An absent block is an unlocked workspace, which is what every running trial and
 * every job trial sends.
 *
 * @param {Object} lock - The bootstrap's `lock` block
 * @returns {{locked: boolean, reason: string, message: string}}
 */
export const readTrialLock = (lock) => ({
    locked: Boolean(lock && lock.locked),
    reason: String((lock && lock.reason) || ''),
    message: String((lock && lock.message) || ''),
});

/* No `versionLabel`. It was read for one chip on the stage head, and a candidate is
   not shown versioning anywhere — so it is not carried either, rather than carried and
   left for the next screen to render by accident. */
export const readCycleContext = (cycle) => ({
    displayTimezone: cycle?.display_timezone || '',
});

const isPlainObject = (value) => Boolean(value) && typeof value === 'object' && !Array.isArray(value);

/**
 * The terminal payload an ended or cancelled cycle trial answers with instead of a workspace.
 * @param {Object} ended - The body's `ended` block
 * @returns {{reason: string, message: string, cohortName: string, endedAt: string,
 *   certificate: ({certificateId: string, issuedAt: string}|null)}}
 */
const readTerminal = (ended) => ({
    reason: String(ended.reason || ''),
    message: String(ended.message || ''),
    cohortName: String(ended.cohort_name || ''),
    endedAt: String(ended.ended_at || ''),
    certificate: isPlainObject(ended.certificate)
        ? {
            certificateId: String(ended.certificate.certificate_id || ''),
            issuedAt: String(ended.certificate.issued_at || ''),
        }
        : null,
    // The exit form after the end, in the feedback read's own three booleans plus the
    // authored prompt. Null from a server that does not send it: the page stays as it was.
    feedback: isPlainObject(ended.feedback)
        ? {
            available: ended.feedback.available === true,
            submitted: ended.feedback.answered === true,
            skipped: ended.feedback.skipped === true,
            prompt: String(ended.feedback.prompt || ''),
        }
        : null,
});

/**
 * The bootstrap payload in the shape the workspace reads it.
 *
 * Two tolerances, both deliberate: the trial may arrive under `work_trial` (the
 * shape every other work-trial route answers in) or as the body itself, and
 * `available_pages` may arrive beside the trial or on it. Everything else is read
 * by its own name and an absent key is an absent surface — never a guess.
 *
 * An ended cycle trial is shut, and its body carries `ended` and no trial. That reads
 * as `{ terminal }` alone, so nothing downstream can compose a workspace from it.
 * @param {Object} payload - The response body
 * @returns {Object} - `{ trial, availablePages, cycle, group, channels, onboarding, piiNudge,
 *   me, nextOpensAt, colleagues, teamForming, notices, terminal: null }`, or `{ terminal }`
 *   for an ended trial
 */
/** The two ways a version forms its teams: with the cohort's start, or later in the trial. */
export const TEAM_FORMING_MODES = Object.freeze(['at_start', 'scheduled']);

/**
 * When this trial's teams form, read off the bootstrap's `team_forming` block (plan §15.4).
 *
 * Sent for a cycle trial on a version with groups and for nothing else, so `null` is every
 * other trial — a solo run, a job trial, a server from before the block existed — and every
 * one of them behaves exactly as it does today. `pending` is the one fact the workspace acts
 * on: the version forms its teams later in the trial and has not yet, so the roster is still
 * to come. A mode this client does not know reads as the start, which is today's path.
 * @param {Object} raw - `{mode, at, formed, formed_at}`
 * @returns {{mode: string, at: string, formed: boolean, formedAt: string, pending: boolean}|null}
 */
export const readTeamForming = (raw) => {
    if (!isPlainObject(raw)) return null;
    const mode = TEAM_FORMING_MODES.includes(raw.mode) ? raw.mode : 'at_start';
    const formed = raw.formed === true;
    return {
        mode,
        at: typeof raw.at === 'string' ? raw.at : '',
        formed,
        formedAt: typeof raw.formed_at === 'string' ? raw.formed_at : '',
        pending: mode === 'scheduled' && !formed,
    };
};

export const readBootstrap = (payload) => {
    const body = payload && typeof payload === 'object' ? payload : {};
    if (isPlainObject(body.ended)) return { terminal: readTerminal(body.ended) };

    const trial = body.work_trial || (body.work_trial_id ? body : null);
    const availablePages = Array.isArray(body.available_pages)
        ? body.available_pages
        : trial?.available_pages;

    return {
        trial,
        availablePages,
        cycle: body.cycle || null,
        // Whether the trial still takes writes, decided by the server. Never absent for
        // a locked trial and never trusted to be present: a portal talking to an older
        // backend reads an unlocked workspace, which is what it had before this existed.
        lock: body.lock || null,
        // The roster, when there is one. Its presence is what makes this trial a
        // group trial, so a solo trial must answer null here and not an empty object.
        group: body.group || null,
        channels: Array.isArray(body.channels) ? body.channels : [],
        onboarding: body.onboarding || {},
        piiNudge: body.pii_nudge_config || {},
        // The name the hiring team wears inside this workspace, configured per
        // workspace (`ChatWorkspace.hr_display_name`). It is the only name the
        // candidate side ever has for them: the real actor behind a reply lives
        // ops-side and no client read reaches it.
        hrDisplayName: String(body.hr_display_name || ''),
        // The viewer as this workspace names them. A trial that sends no block of
        // its own is naming the same person its profile map already names.
        me: body.me || trial?.profile || {},
        // Present only when the server is serving an admin view, and null for every
        // candidate. It carries the uid of the candidate being viewed, which this tab
        // cannot get from a Firebase session it does not have — the chat transport needs
        // it to open a lane at all, and `isMine` needs it to tell the candidate's own
        // messages from everybody else's. It has to be on this list: the allow-list is
        // the whole reason the workspace renders nothing it was not handed, and a key
        // missing from it is a key the workspace never sees.
        adminView: body.admin_view || null,
        // When anything not yet shown opens (contract C1): an ISO instant, `''` when
        // nothing further opens, and null from a server that does not send it.
        nextOpensAt: Object.prototype.hasOwnProperty.call(body, 'next_opens_at')
            ? String(body.next_opens_at || '')
            : null,
        // The version's AI colleagues, sent only when it has any; read through
        // `colleagues/model.js`. Absent is none, which is every trial before them.
        colleagues: Array.isArray(body.colleagues) ? body.colleagues : [],
        // When the teams form, for a cycle trial on a grouped version (plan §15.4). Null
        // for every other trial, which is also every trial from before the block existed.
        teamForming: readTeamForming(body.team_forming),
        // Video windows, work due soon and which pop-ups were answered (CONTRACT-notices
        // §2). Null from a server that does not send it; the site then builds both lists
        // from its own reads.
        notices: isPlainObject(body.notices) ? body.notices : null,
        terminal: null,
    };
};

/**
 * @param {string} workTrialId
 * @returns {Promise<Object>} - The normalized bootstrap
 */
export const getWorkspaceBootstrap = async (workTrialId) => {
    const response = await API.get(bootstrapPath(workTrialId));
    return readBootstrap(response?.data);
};

/** The brand-logo route, written once so nothing else spells it. */
export const brandLogoPath = (workTrialId) => `work-trials/${workTrialId}/brand-logo`;

/**
 * A short-lived read URL for the logo this trial's kit wears, or `''` when it wears none.
 *
 * The brand-assets bucket is private, so the snapshot's `logo_gcs_path` is not an address
 * and never was — the workspace used to build a `storage.googleapis.com` URL from it and
 * take a silent 403. The server mints a V4 signature over the object its *own* read of the
 * trial's frozen snapshot resolves, which is why this is keyed on the trial and takes no
 * kit id: a candidate cannot ask for a kit that is not the one they are wearing.
 *
 * 404 is the ordinary answer for a kit with no mark, and it is a value rather than a
 * throw: nothing has gone wrong, there is simply a glyph to wear instead. Every other
 * failure throws, because those are worth one retry.
 *
 * `KEEP_DRAFT` for the same reason a composer send carries it: a lease renews on a timer,
 * in the background, hours into a session, and a 24px mark is never a good enough reason
 * to throw somebody off the page they are working on. Anything the workspace actually
 * needs an auth answer for — the bootstrap, the heartbeat — still redirects.
 *
 * @param {string} workTrialId
 * @returns {Promise<{url: string, expiresInMinutes: number}>}
 */
export const getTrialBrandLogo = async (workTrialId) => {
    try {
        const response = await API.get(brandLogoPath(workTrialId), KEEP_DRAFT);
        return {
            url: String(response?.data?.logo_url || ''),
            expiresInMinutes: Number(response?.data?.expires_in_minutes) || 0,
        };
    } catch (error) {
        if (error?.response?.status === 404) return { url: '', expiresInMinutes: 0 };
        throw error;
    }
};

/**
 * The fallback, used only when the bootstrap carries no cycle block. It is the
 * candidate's own enrolment list, so a failure here costs the version label and
 * the zone and never the workspace.
 * @param {string} cycleId
 * @returns {Promise<Object|null>} - The cycle row, or null when it has aged out
 */
export const fetchEnrolmentCycle = async (cycleId) => {
    try {
        const rows = await getMyEnrolments();
        return rows.find((row) => row.cycle?.cycle_id === cycleId)?.cycle || null;
    } catch (error) {
        console.error('Error fetching cycle context:', error);
        return null;
    }
};

/**
 * The delivered-event feed, and the only path in this app that names it.
 *
 * It reads `work-trials/{id}/trial-events` server-side — the candidate-safe event
 * instances, already audience-filtered by the events section's `affects` rule. That
 * subcollection is the only feed this app knows of: the ops twin beside it is not
 * client-readable and is not named anywhere in this tree, and no other collection
 * carries delivered events under any other name.
 */
export const feedPath = (workTrialId) => `work-trials/${workTrialId}/feed`;

/** The presence beat's write. The read is group-scoped and lives below it. */
export const presenceHeartbeatPath = (workTrialId) =>
    `work-trials/${workTrialId}/presence/heartbeat`;

/** Who else is here, computed server-side — the client never dates a `last_seen`. */
export const groupPresencePath = (workTrialId) => `work-trials/${workTrialId}/group/presence`;

/**
 * The candidate-safe event instance's whole field list (Section 4 task 15's
 * projection). The workspace renders from this and from nothing else: a key the
 * server adds later is not rendered until this list says it is, so an ops field
 * that ever escaped the projection would be invisible rather than displayed.
 */
export const FEED_FIELDS = Object.freeze([
    'event_id',
    'event_type',
    'title',
    'body',
    'attachments',
    'channel_id',
    'sender_display_name',
    'affects',
    'published_at',
    'requires_ack',
    'supersedes',
    'sent_at',
    'first_viewed_at',
]);

/**
 * One feed row, reduced to the fields above.
 * @param {Object} row - An event instance as the feed endpoint projects it
 * @returns {Object} - The same row carrying no key outside `FEED_FIELDS`
 */
export const projectFeedRow = (row) => {
    const safe = {};
    FEED_FIELDS.forEach((field) => {
        if (row && Object.prototype.hasOwnProperty.call(row, field)) safe[field] = row[field];
    });
    return safe;
};

/**
 * The rows of a feed response, whichever envelope carries them. Anything that is
 * not a list of objects is no rows at all rather than a guess.
 * @param {Object|Array} payload - The response body
 * @returns {Array<Object>} - The projected rows, in the order they arrived
 */
export const readFeedRows = (payload) => {
    const rows = Array.isArray(payload)
        ? payload
        : payload?.events || payload?.feed || payload?.trial_events;

    if (!Array.isArray(rows)) return [];
    return rows.filter((row) => row && typeof row === 'object').map(projectFeedRow);
};

/**
 * The feed since the cursor. An absent cursor asks for the whole history, which is
 * what the first read of a session does.
 * @param {string} workTrialId
 * @param {string} [since] - The newest `published_at` already held
 * @returns {Promise<Array<Object>>}
 */
export const fetchFeed = async (workTrialId, since = '') => {
    const response = await API.get(feedPath(workTrialId), {
        ...KEEP_DRAFT,
        params: since ? { since } : undefined,
    });
    return readFeedRows(response?.data);
};

/**
 * Where an event-backed unit's reading is recorded.
 *
 * Receipts are fields on the candidate-safe instance — instances are per-trial,
 * so an instance field is already per-candidate — and this is the generic
 * mark-viewed endpoint that writes them. It is the only lane an event-backed unit
 * takes: the Q&A batch carries questions and FAQ entries and never events, so
 * there is one home per kind of unit rather than two answers for one.
 */
export const eventReceiptPath = (workTrialId, eventId) =>
    `work-trials/${workTrialId}/feed/${encodeURIComponent(eventId)}/receipt`;

/**
 * Record that an event was read, or scrolled past.
 *
 * Best-effort and silent, like the activity log: `first_viewed_at` is write-once
 * server-side, so a second call is a no-op there and a failed one costs a receipt
 * rather than anything the candidate is doing. Nothing in the UI reflects that
 * this was posted.
 *
 * @param {string} workTrialId
 * @param {string} eventId
 * @param {Object} [receipt] - `{durationSeconds, scrolledPast, acknowledged}`
 * @returns {Promise<void>}
 */
export const postEventReceipt = async (workTrialId, eventId, receipt = {}) => {
    if (!workTrialId || !eventId) return;

    // Milliseconds under the name the reader reads. `read_event_receipt` takes
    // `visible_ms` and divides it down itself, exactly as the unload batch's rows
    // are read, so a duration spelled any other way records a view of zero.
    const body = {
        visible_ms: Math.max(0, Math.round((receipt.durationSeconds || 0) * 1000)),
        scrolled_past: Boolean(receipt.scrolledPast),
    };
    if (receipt.acknowledged) body.acknowledged = true;

    try {
        await API.post(eventReceiptPath(workTrialId, eventId), body, KEEP_DRAFT);
    } catch (error) {
        // Silent by design.
    }
};

/** Where a pop-up is recorded as answered, so it is never raised again. */
export const noticeSeenPath = (workTrialId, key) =>
    `work-trials/${workTrialId}/notices/${encodeURIComponent(key)}/seen`;

/**
 * Records a notice pop-up as seen. Idempotent server-side, and silent: the browser
 * keeps its own record too, so a failed write never brings the pop-up back here.
 * @param {string} workTrialId
 * @param {string} key - `video-open:<id>` or `due-soon:<YYYY-MM-DD>`
 * @returns {Promise<void>}
 */
export const postNoticeSeen = async (workTrialId, key) => {
    if (!workTrialId || !key) return;
    try {
        await API.post(noticeSeenPath(workTrialId, key), {}, KEEP_DRAFT);
    } catch (error) {
        // Silent by design.
    }
};

/**
 * "Still here." Two fields land server-side and the reader decides what online
 * means, so there is nothing to send but the beat itself.
 * @param {string} workTrialId
 * @returns {Promise<void>}
 */
export const sendPresenceHeartbeat = (workTrialId) =>
    API.post(presenceHeartbeatPath(workTrialId), {}, KEEP_DRAFT);

/**
 * Any workspace surface, by its own path. The sync engine holds the paths (see
 * `hooks/useWorkspaceSync.js`); this is the one place they are actually issued, so
 * every background read carries the same "never bounce me to /login" contract.
 * @param {string} path - A candidate-API path
 * @returns {Promise<*>} - The response body
 */
export const fetchWorkspaceResource = async (path) => {
    const response = await API.get(path, KEEP_DRAFT);
    return response?.data;
};

/**
 * One activity log line. Detectors read these, so the write is best-effort and
 * silent: a log that fails is a log, and a log that redirects would be a candidate
 * thrown out of a trial by a background request.
 * @param {string} workTrialId
 * @param {string} category
 * @param {string} action
 * @param {Object} [metadata] - Merged over the ambient session/viewport facts
 * @returns {Promise<void>}
 */
export const logWorkspaceActivity = async (workTrialId, category, action, metadata = {}) => {
    if (!workTrialId || !isAuthenticated()) return;

    try {
        await API.post(
            `work-trials/${workTrialId}/logs/add`,
            {
                category,
                action,
                metadata: {
                    timestamp: new Date().toISOString(),
                    user_agent: navigator.userAgent,
                    screen_width: window.screen.width,
                    screen_height: window.screen.height,
                    window_width: window.innerWidth,
                    window_height: window.innerHeight,
                    ...metadata,
                },
            },
            KEEP_DRAFT
        );
    } catch (error) {
        // Silent by design: nothing a candidate is doing depends on this landing.
    }
};

/* ------------------------------------------------------------------------- */
/* Onboarding modules                                                         */
/* ------------------------------------------------------------------------- */

/**
 * The typed onboarding-module array, joined server-side to this candidate's
 * per-module completion state (`work-trials/{id}/onboarding-progress/{module_id}`).
 *
 * The workspace reads it twice over: once on the bootstrap, which is what the
 * first paint is drawn from, and then on the refresh contract's tick, which is
 * what makes a module that unlocks mid-trial — or an acknowledgement cleared by
 * the hiring team — arrive without a reload.
 */
export const onboardingPath = (workTrialId) => `work-trials/${workTrialId}/onboarding`;

/** Where an acknowledgement is recorded. One module, one completion, once per trial. */
export const onboardingModuleCompletePath = (workTrialId, moduleId) =>
    `work-trials/${workTrialId}/onboarding/modules/${moduleId}/complete`;

/**
 * The module rows of either read, whichever envelope carries them.
 *
 * The array is canonical as `{module_id, type, order, title, body, config}`; the
 * completion join arrives beside it. Anything that is not a list of objects is no
 * modules at all rather than a guess — a trial whose structure authored none is a
 * trial with no onboarding, not a trial with a broken read.
 * @param {Object|Array} payload
 * @returns {Array<Object>}
 */
export const readOnboardingModules = (payload) => {
    const rows = Array.isArray(payload) ? payload : payload?.modules;
    if (!Array.isArray(rows)) return [];
    return rows.filter((row) => row && typeof row === 'object');
};

/**
 * A module's completion state: the row filed under its id, or `null`.
 *
 * The server joins each module's completion onto that module's own row, so there is
 * no second map to consult — and an id no row carries has nothing recorded against it.
 * @param {Object|Array} payload
 * @param {string} moduleId
 * @returns {Object|null}
 */
export const completionFor = (payload, moduleId) => {
    if (!moduleId) return null;
    return readOnboardingModules(payload)
        .find((row) => String(row.module_id) === String(moduleId)) || null;
};

/**
 * Record one module as done.
 *
 * It carries the keep-the-draft contract for the same reason a send does: this is
 * posted from inside a dialog somebody is reading, and a transient 401 must come
 * back as a failure the caller can shrug off rather than as a redirect out of the
 * workspace.
 * @param {string} workTrialId
 * @param {string} moduleId
 * @returns {Promise<*>}
 */
export const completeOnboardingModule = (workTrialId, moduleId) =>
    API.post(onboardingModuleCompletePath(workTrialId, moduleId), {}, KEEP_DRAFT);

/* ------------------------------------------------------------------------- */
/* The board                                                                  */
/* ------------------------------------------------------------------------- */

/**
 * Where a card's column is stored.
 *
 * Almost every column a card sits in is derived — a submitted step is in review or
 * done because it was submitted, not because anybody dragged it — and derived
 * states win. The one move a candidate makes is To do ↔ In progress, which is a
 * statement about what they are working on and nothing the server can infer, so it
 * is the one move that is written down. It is written here and read back on the
 * projects payload; the board never keeps a column of its own.
 */
export const boardMovePath = (workTrialId) => `work-trials/${workTrialId}/board/move`;

/**
 * The three columns a candidate owns. A move to any other column is a move to a
 * state the work itself decides, so it is refused before it reaches the wire.
 *
 * Blocked is one of the three because being stuck is not something the work says
 * about itself: nothing the server can read off a submission tells it that a
 * candidate cannot get on with a step, so the candidate says it, and the reason
 * they give travels with the column rather than after it.
 */
export const BOARD_MOVE_STATUSES = Object.freeze(['todo', 'doing', 'blocked']);

/**
 * Move a card.
 *
 * The address is the step, spelled the way every other addressed thing in this
 * program is: the pair that names it, never the card id the board happens to draw
 * it under. The implicit single step of a legacy project sends `step_id: ''`,
 * which is the API sentinel for it everywhere.
 *
 * It carries the keep-the-draft contract because a transient 401 on a background
 * write must come back as a failure the board can shrug off — the card springs
 * back — rather than as a candidate bounced out of their workspace mid-drag.
 *
 * @param {string} workTrialId
 * @param {Object} move
 * @param {string} move.projectId
 * @param {string} move.stepId - `''` for a project with no authored steps
 * @param {string} move.status - One of `BOARD_MOVE_STATUSES`
 * @param {string} [move.blockedReason] - Why the work has stopped, sent only with
 *   the column it explains: a reason beside any other column is a reason for a
 *   block that is not happening, which is what the card route refuses outright.
 * @returns {Promise<*>}
 */
export const postBoardMove = (workTrialId, { projectId, stepId = '', status, blockedReason = '' }) => {
    if (!BOARD_MOVE_STATUSES.includes(status)) {
        return Promise.reject(new Error(`unmovable column: ${status}`));
    }

    return API.post(
        boardMovePath(workTrialId),
        {
            project_id: projectId,
            step_id: stepId,
            status,
            ...(status === 'blocked' && blockedReason
                ? { blocked_reason: String(blockedReason) }
                : {}),
        },
        KEEP_DRAFT
    );
};

/* ------------------------------------------------------------------------- */
/* The candidate's own cards                                                  */
/* ------------------------------------------------------------------------- */

/**
 * The board a candidate writes, beside the one they were given.
 *
 * An authored step is a card because somebody authored it, and its column is one
 * key of the project it belongs to (`postBoardMove` above). A card the candidate
 * invented — "split the importer", "chase the missing fixture" — belongs to no
 * authored step, so it has a document of its own in `board-cards` and these five
 * routes are how it is made, edited, claimed, dropped and moved. One board is drawn
 * out of both reads; nothing here mirrors the other half or writes into it.
 *
 * Two rules of the server's, held here so a control cannot be talked past them:
 *
 *   **A card's column only ever changes through the move.** Create and edit both
 *   refuse a `status` outright — not ignore it — because the move is what files the
 *   change-log row saying the card was ever in the other column. So `status` is
 *   never a key in a create or an edit body, not even as null.
 *
 *   **A claim is for yourself or it is a release.** `claimed_by` may carry the
 *   caller's own uid or the empty string and nothing else: taking a card is taking
 *   it, and putting somebody else's id on one would be handing a teammate work.
 */
export const boardCardsPath = (workTrialId) => `work-trials/${workTrialId}/board/cards`;

export const boardCardPath = (workTrialId, cardId) =>
    `work-trials/${workTrialId}/board/cards/${cardId}`;

export const boardCardMovePath = (workTrialId, cardId) =>
    `${boardCardPath(workTrialId, cardId)}/move`;

/** Every field the server sends about one card, and nothing it does not. */
export const BOARD_CARD_FIELDS = Object.freeze([
    'card_id', 'project_id', 'step_id', 'part_id', 'title', 'description', 'origin',
    'status', 'blocked_reason', 'claimed_by', 'claimed_at', 'created_by', 'created_at', 'order',
]);

/**
 * One card as this client holds it: the server's own field names, read through the
 * allow-list so a key the board never draws cannot arrive and be rendered by
 * accident. An absent string is `''` and an absent order is `0`, which is what the
 * model itself defaults them to — the card a create answers with and the card a
 * later list read carries are then the same shape.
 * @param {Object} row
 * @returns {Object}
 */
export const projectBoardCard = (row) => {
    const card = row && typeof row === 'object' ? row : {};
    return BOARD_CARD_FIELDS.reduce((held, key) => ({
        ...held,
        [key]: key === 'order' ? Number(card[key]) || 0 : String(card[key] || ''),
    }), {});
};

/** The cards of a board read, whichever envelope carries them, in the server's order. */
export const readBoardCards = (payload) => {
    const rows = Array.isArray(payload) ? payload : payload?.cards;
    return (Array.isArray(rows) ? rows : [])
        .filter((row) => row && typeof row === 'object')
        .map(projectBoardCard)
        .filter((card) => card.card_id);
};

/**
 * @param {string} workTrialId
 * @returns {Promise<Array<Object>>}
 */
export const fetchBoardCards = async (workTrialId) => {
    const response = await API.get(boardCardsPath(workTrialId));
    return readBoardCards(response?.data);
};

/**
 * The body one create sends — the six keys the route reads, and no seventh.
 *
 * `origin`, `claimed_by`, `blocked_reason`, `created_by` and `card_id` are the
 * server's to decide and `status` is refused, so a draft that carried any of them
 * would either be ignored or turn a create into a 400. The title is sent as typed;
 * the server strips it, because a title is an identifier read down a column.
 * @param {Object} draft
 * @returns {Object}
 */
export const boardCardBody = ({
    title = '', description = '', projectId = '', stepId = '', partId = '', order = 0,
} = {}) => ({
    title: String(title),
    description: String(description),
    project_id: String(projectId),
    step_id: String(stepId),
    part_id: String(partId),
    order: Number(order) || 0,
});

/** Why this card cannot be written, or `''`. A card needs a title and nothing else. */
export const boardCardRefusal = ({ title = '' } = {}) =>
    (String(title).trim() ? '' : 'A card needs a title');

/**
 * The sentence a refused board-card write is reported with.
 *
 * The server writes one per refusal code and they are written for the candidate —
 * "A card needs a title", "That project is not part of this work trial" — so its
 * own words are preferred to anything spelled here. The fallback is for the
 * failures that carry no body at all: a dropped connection is not a refusal.
 * @param {Error} error
 * @param {string} [fallback]
 * @returns {string}
 */
export const boardCardError = (error, fallback = 'That did not save. Try again in a moment.') => (
    isSignedOutError(error)
        ? SIGNED_OUT_LINE
        : String(error?.response?.data?.error || '') || fallback
);

/**
 * Make one card. It starts in To do, because that is where a card starts.
 * @param {string} workTrialId
 * @param {Object} draft - `{title, description, projectId, stepId, partId, order}`
 * @returns {Promise<Object>} - The card the server minted, its own id included
 */
export const createBoardCard = async (workTrialId, draft) => {
    const refusal = boardCardRefusal(draft);
    if (refusal) {
        // Held where it cannot be clicked past, the way a request's own guard is.
        return Promise.reject(new Error(refusal));
    }

    const response = await API.post(boardCardsPath(workTrialId), boardCardBody(draft), KEEP_DRAFT);
    const body = response?.data || {};
    return projectBoardCard(body.card || body);
};

/**
 * Edit one card: retitle it, rewrite its description, claim or release it, clear the
 * reason a block is over, reorder it.
 *
 * Only the keys named are sent. A key the server does not receive means "keep what
 * is stored" — it takes a complete model and lays the patch over it — so sending
 * the whole card back would blank every field this form does not draw.
 *
 * @param {string} workTrialId
 * @param {string} cardId
 * @param {Object} changes - Any of `{title, description, blockedReason, claimedBy, order}`
 * @returns {Promise<Object>} - The card as it now stands
 */
export const patchBoardCard = async (workTrialId, cardId, changes = {}) => {
    const body = {};
    if (changes.title !== undefined) body.title = String(changes.title);
    if (changes.description !== undefined) body.description = String(changes.description);
    if (changes.blockedReason !== undefined) body.blocked_reason = String(changes.blockedReason);
    if (changes.claimedBy !== undefined) body.claimed_by = String(changes.claimedBy);
    if (changes.order !== undefined) body.order = Number(changes.order) || 0;

    const response = await API.patch(boardCardPath(workTrialId, cardId), body, KEEP_DRAFT);
    const answer = response?.data || {};
    return projectBoardCard(answer.card || answer);
};

/**
 * Take this card, or put it back.
 *
 * The uid is the caller's own and the server checks that it is: a claim is the one
 * write on this surface that names a person, and the only person it may name is the
 * one making it. An empty uid is a release.
 * @param {string} workTrialId
 * @param {string} cardId
 * @param {string} uid - The caller's own uid, or `''` to release
 * @returns {Promise<Object>}
 */
export const claimBoardCard = (workTrialId, cardId, uid = '') =>
    patchBoardCard(workTrialId, cardId, { claimedBy: uid });

/**
 * Drop one card the candidate made. The card is gone; the change log keeps what it
 * was, which is the only place its title and column survive.
 * @param {string} workTrialId
 * @param {string} cardId
 * @returns {Promise<string>} - The id that is now free
 */
export const removeBoardCard = async (workTrialId, cardId) => {
    const response = await API.delete(boardCardPath(workTrialId, cardId), KEEP_DRAFT);
    return String(response?.data?.card_id || cardId);
};

/**
 * Move one card into another column, with the reason if the column is Blocked.
 *
 * A route of its own and not a field on the edit, because the move is what files
 * the row saying the card was ever where it was. The reason travels with it for the
 * same reason it does on a step: the column and the account of it are one
 * statement, and a card leaving Blocked sheds its reason in the same write.
 *
 * @param {string} workTrialId
 * @param {string} cardId
 * @param {Object} move - `{status, blockedReason}`
 * @returns {Promise<{card_id: string, status: string, blocked_reason: string}>}
 */
export const moveBoardCard = async (workTrialId, cardId, { status, blockedReason = '' } = {}) => {
    if (!BOARD_MOVE_STATUSES.includes(status)) {
        return Promise.reject(new Error(`unmovable column: ${status}`));
    }

    const response = await API.post(
        boardCardMovePath(workTrialId, cardId),
        {
            status,
            ...(status === 'blocked' ? { blocked_reason: String(blockedReason) } : {}),
        },
        KEEP_DRAFT
    );
    const body = response?.data || {};
    return {
        card_id: String(body.card_id || cardId),
        status: String(body.status || status),
        blocked_reason: String(body.blocked_reason || ''),
    };
};

/* ------------------------------------------------------------------------- */
/* Profile, and the preset marks                                              */
/* ------------------------------------------------------------------------- */

/**
 * The candidate's own display identity on this trial — read and written here and
 * nowhere else.
 *
 * It is one map on the trial, `WorkTrial.profile`, and never a set of flat fields
 * beside it: no top-level display-name field, no top-level avatar field, and no
 * second snapshot carrying a copy of the same three answers. That is what makes
 * "who am I in this workspace" a single answer rather than three that can
 * disagree — and it is asserted as a grep, so this comment may not name the
 * fields it is refusing.
 */
export const profilePath = (workTrialId) => `work-trials/${workTrialId}/onboarding/profile`;

/**
 * The preset mark library.
 *
 * Avatars in this workspace are a **fixed preset library** and nothing else: no
 * upload control exists anywhere in this app, and the set is not HR-customisable.
 * The list is the backend's own code constant, served from here, so the client
 * offers exactly the ids the write path will accept and invents none of its own.
 */
export const avatarsPath = (workTrialId) => `work-trials/${workTrialId}/onboarding/avatars`;

/**
 * The whole of `WorkTrial.profile`, in the order the map declares them.
 *
 * This is the map as it is *stored and read back* — it is not what a save sends.
 * Two of the five are the server's own record rather than answers a candidate
 * gives: `timezone_source` is how the zone was arrived at and `profile_set` is
 * the fact that the form was finished, and the write route refuses a body that
 * asserts either. What may be sent is `PROFILE_INPUT_KEYS`, below.
 */
export const PROFILE_KEYS = Object.freeze([
    'display_name',
    'avatar_id',
    'timezone',
    'timezone_source',
    'profile_set',
]);

/**
 * The three keys a candidate may actually assert about themselves.
 *
 * The backend refuses a body carrying anything outside these — a fourth key is a
 * 400 rather than a silent drop, deliberately, so that a client cannot come away
 * believing it set something the surface does not collect. Sending the stored
 * map back verbatim is exactly that mistake, and it is what used to make the
 * "Before you start" dialog answer "That did not save."
 *
 * `avatar_id` is also value-checked against the preset library, so an unset mark
 * is sent by *omitting the key* rather than by sending `''`: the write merges, so
 * a key left out keeps whatever the map already held, while `''` is a mark that
 * is not on offer and is refused as one.
 */
export const PROFILE_INPUT_KEYS = Object.freeze(['display_name', 'avatar_id', 'timezone']);

/**
 * The preset rows of the avatars read, whichever envelope carries them.
 *
 * A row may arrive as a bare id or as `{avatar_id, label}`; anything without an id
 * is not a preset. An empty answer is an answer — a workspace whose library the
 * server has not filled in yet simply has no marks to choose from, and the
 * candidate keeps whichever id their profile already holds.
 * @param {Object|Array} payload
 * @returns {Array<{avatar_id: string, label: string}>}
 */
export const readAvatarPresets = (payload) => {
    const rows = Array.isArray(payload) ? payload : payload?.avatars;
    if (!Array.isArray(rows)) return [];

    return rows
        .map((row) => (typeof row === 'string'
            ? { avatar_id: row, label: '' }
            : { avatar_id: String(row?.avatar_id || ''), label: String(row?.label || '') }))
        .filter((row) => row.avatar_id);
};

/**
 * The library, or none.
 *
 * A failed read costs the choice of mark and never the gate behind it: the setup
 * form still takes a name and a timezone, and the profile keeps the `avatar_id` it
 * already had. Being unable to pick a colour must not be able to strand somebody
 * on their first morning.
 * @param {string} workTrialId
 * @returns {Promise<Array<{avatar_id: string, label: string}>>}
 */
export const fetchAvatarPresets = async (workTrialId) => {
    try {
        const response = await API.get(avatarsPath(workTrialId), KEEP_DRAFT);
        return readAvatarPresets(response?.data);
    } catch (error) {
        return [];
    }
};

/**
 * Write the profile map.
 *
 * The body carries what the candidate answered and nothing else. `profile_set`
 * and `timezone_source` are not fields the form collects — the first is what
 * completing the form *means* and the second is the server's record of how the
 * zone was arrived at — so they are stamped by the write route, which refuses a
 * body that tries to assert either. A mark the candidate has not picked is left
 * out rather than sent blank, because the write merges and `''` is not a preset.
 *
 * What comes back is the server's map, not the one that went out: it is the only
 * thing that knows the two stamped keys, and `profile_set` is what lifts the
 * first-visit gate on the press. A build that answers without one falls back to
 * what was sent plus that stamp, so the gate still lifts.
 *
 * It carries the keep-the-draft contract: this is posted from inside a dialog
 * somebody is typing in, and a transient 401 must come back as a failure the
 * dialog can report rather than as a redirect that throws the typing away.
 *
 * @param {string} workTrialId
 * @param {Object} profile - `{display_name, avatar_id, timezone}`
 * @returns {Promise<Object>} - The stored map, as the server holds it
 */
export const saveWorkspaceProfile = async (workTrialId, profile = {}) => {
    const body = {
        display_name: String(profile.display_name || ''),
        timezone: String(profile.timezone || ''),
    };

    const avatarId = String(profile.avatar_id || '');
    if (avatarId) body.avatar_id = avatarId;

    const response = await API.put(profilePath(workTrialId), body, KEEP_DRAFT);
    return response?.data?.profile || { ...body, profile_set: true };
};
